import { useQuery } from '@apollo/client';
import { IMAGE_FILE_TYPES } from 'Components/FileUpload/Dropzone';
import { EFilterType, TFilter, TFilterEvent } from 'Components/Filter/filter.types';
import useDialog from 'hooks/useDialog';
import { useLogEvent } from 'hooks/useLogEvent';
import { DOCUMENTS_PER_PAGE } from 'utils/pagination.utils';
import { GET_SHARED_DOCUMENTS } from 'graphql/secure/queries';
import moment from 'moment';
import { useMemo } from 'react';
import { EDocumentCategory, IBaseDocument } from 'FeatureModules/Document/types/document.types';
import { ERequestType } from 'FeatureModules/Partner/types/partner.types';
import { IGraphQlConnection } from 'types/core.types';
import {
  ArrayParam,
  createEnumArrayParam,
  ObjectParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';

const DATE_FORMAT = 'YYYY-MM-DD';

const useSharedDocuments = () => {
  const [query, setQuery] = useQueryParams(
    {
      searchTerm: withDefault(StringParam, ''),
      creationDateRange: ObjectParam,
      categories: withDefault(createEnumArrayParam(Object.keys(EDocumentCategory)), null),
      siteIds: withDefault(ArrayParam, undefined),
    },
    { removeDefaultsFromUrl: true }
  );

  const { searchTerm, creationDateRange, categories, siteIds } = query;

  const { data, loading, error, fetchMore } = useQuery<{
    sharedDocuments: IGraphQlConnection<IBaseDocument>;
  }>(GET_SHARED_DOCUMENTS, {
    variables: {
      filters: {
        searchTerm,
        creationDateRange,
        categories,
        siteIds,
        fileExtensions: ['pdf', ...IMAGE_FILE_TYPES],
      },
      first: DOCUMENTS_PER_PAGE,
    },
  });
  const { openDialog } = useDialog();
  const { logEvent } = useLogEvent();

  const documents: IBaseDocument[] = useMemo(
    () => (data?.sharedDocuments ? removeGraphConnections(data?.sharedDocuments) : []),
    [data]
  );

  const hasNextPage: boolean = !!data?.sharedDocuments?.pageInfo?.hasNextPage;
  const endCursor: string | undefined = data?.sharedDocuments?.pageInfo?.endCursor;

  const filters: TFilter[] = useMemo(
    () => [
      {
        type: EFilterType.DOCUMENT_CATEGORY,
        value: categories,
      },
      {
        type: EFilterType.SITES,
        value: (siteIds?.filter(Boolean) || undefined) as string[] | undefined,
      },
      {
        type: EFilterType.TIME_FRAME,
        label: 'Upload date',
        value:
          creationDateRange?.startDate || creationDateRange?.endDate
            ? {
                startDate: creationDateRange?.startDate
                  ? new Date(creationDateRange.startDate)
                  : undefined,
                endDate: creationDateRange?.endDate
                  ? new Date(creationDateRange.endDate)
                  : undefined,
              }
            : null,
      },
    ],
    [categories, creationDateRange, siteIds]
  );

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  const handleNewDocumentRequestClick = () =>
    openDialog({ type: 'PARTNER_REQUEST', props: { requestType: ERequestType.DOCUMENT } });

  const handleFilterChange = (event: TFilterEvent) => {
    switch (event.type) {
      case EFilterType.TIME_FRAME: {
        const { startDate, endDate } = event.value || {};

        setQuery({
          creationDateRange: {
            startDate: startDate ? moment(startDate).format(DATE_FORMAT) : undefined,
            endDate: endDate ? moment(endDate).format(DATE_FORMAT) : undefined,
          },
        });
        break;
      }
      case EFilterType.DOCUMENT_CATEGORY: {
        setQuery({
          ...event,
          categories: event.value,
        });
        break;
      }
      case EFilterType.SITES: {
        setQuery({
          ...event,
          siteIds: event.value || undefined,
        });
        break;
      }
      default:
        break;
    }
    logEvent('INTERNAL_DOCUMENTS_FILTERED', { type: event.type });
  };

  const handleFilterReset = () => {
    setQuery({
      searchTerm: '',
      creationDateRange: undefined,
      categories: undefined,
      siteIds: undefined,
    });
  };

  const handleSearchTermChange = (searchTerm: string) => {
    setQuery({ searchTerm });
    logEvent('INTERNAL_DOCUMENTS_SEARCHED', { searchTerm });
  };

  return {
    error,
    loading,
    documents,
    searchTerm,
    endCursor,
    hasNextPage,
    filters,
    handleFilterChange,
    handleFilterReset,
    handleSearchTermChange,
    handlePageEndReached,
    handleNewDocumentRequestClick,
  };
};

export default useSharedDocuments;
